import React from "react";
import { useDiorama } from "../../../contexts/DioramaContext";
import CharacterPreview from "../CharacterPreview";
import ReactDOM from "react-dom/client";

export const ExportComponentAsSVG = async (
	component: React.ReactElement,
	filename: string = "character",
) => {
	const tempContainer = document.createElement("div");
	document.body.appendChild(tempContainer);

	const root = ReactDOM.createRoot(tempContainer);
	root.render(component);

	await new Promise((resolve) => setTimeout(resolve, 300));

	const svgElement = tempContainer.querySelector("svg");
	if (!svgElement) {
		console.error("No SVG element found");
		document.body.removeChild(tempContainer);
		return;
	}

	const svgClone = svgElement.cloneNode(true) as SVGElement;
	const svgContent = svgClone.outerHTML;

	// Create a blob from the SVG string
	const blob = new Blob(
		['<?xml version="1.0" encoding="UTF-8" ?>', svgContent],
		{ type: "image/svg+xml" },
	);

	// Clean up
	root.unmount();
	document.body.removeChild(tempContainer);

	const url = URL.createObjectURL(blob);

	// Create download link
	const link = document.createElement("a");
	link.href = url;
	link.download = `${filename}.svg`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	// Clean up
	URL.revokeObjectURL(url);
};

const FinalReview: React.FC = () => {
	const { state, encodeToUrl } = useDiorama();
	const [isCopied, setIsCopied] = React.useState(false);

	const shareUrl = encodeToUrl();

	const handleCopyLink = () => {
		navigator.clipboard.writeText(shareUrl);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};

	const handleExportCharacter = (characterComponent: React.ReactElement) => {
		// Export the character using the existing function
		ExportComponentAsSVG(
			characterComponent,
			`character-${characterComponent.key}`,
		);
	};

	return (
		<div>
			<h2 className="text-xl font-semibold mb-4">Your Diorama is Complete!</h2>
			<p className="mb-6">
				Here's your finished diorama. Share it using the link below.
			</p>

			<div className="mb-6">
				<h3 className="font-medium mb-2">Share Link:</h3>
				<div className="flex flex-col md:flex-row">
					<input
						type="text"
						readOnly
						value={shareUrl}
						className="flex-1 p-2 border rounded-l focus:outline-none focus:ring focus:ring-blue-300 bg-white"
					/>
					<button
						type="submit"
						onClick={handleCopyLink}
						className="cursor-pointer bg-amber-400 text-white px-4 py-2 rounded-r hover:bg-amber-600 focus:bg-amber-900"
					>
						{isCopied ? "Copied!" : "Copy"}
					</button>
				</div>
			</div>

			<div className="space-y-4">
				<h3 className="font-medium">Diorama Summary:</h3>
				<ul className="pl-5 space-y-2 list-none">
					<li>
						✅ Diorama:{" "}
						{state.background ? `${state.background.name}` : "❌ None"}
					</li>
					<li>
						✅ Characters:{" "}
						{state.characters.map((c) => {
							const char = (
								<CharacterPreview
									key={c.id}
									template={c.customization?.template!}
									skinTone={c.customization?.skinTone!}
									top={c.customization?.top}
									bottom={c.customization?.bottom}
									accessories={c.customization?.accessories}
									iris={c.customization?.irisColor!}
									hair={c.customization?.hair}
									size="sm"
								/>
							);
							return (
								<button
									key={c.id}
									type="button"
									className="cursor-pointer inline-block"
									onClick={() => handleExportCharacter(char)}
								>
									{char}
								</button>
							);
						})}
					</li>
					<li>✅ Message: {state.text ? `"${state.text}"` : "None"}</li>
				</ul>
			</div>
			<hr className="mt-5 border-gray-300" />
			<div className="mt-8 text-center">
				<button
					type="reset"
					onClick={() => {
						window.location.href = window.location.pathname;
					}}
					className="px-6 py-2 bg-amber-500 text-white rounded hover:bg-amber-600"
				>
					Create Another Diorama
				</button>
			</div>
		</div>
	);
};

export default FinalReview;
